import React, { useMemo, useEffect } from "react";
import useSWR from "swr";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import SkeletonTable from "../../Common/SkeletonTable";
import ErrorCard from "../../Common/ErrorCard";
import EmptyCard from "../../Common/EmptyCard";
import PaginationBar from "../../Common/PaginationBar";
import { inventoryFetcher } from "../../../network/InventoryService";
import { DEFAULT_PAGE_SIZE } from "../../../utils/inventoryHelper";

const DHList = ({
  onDHView,
  searchTerm,
  circleFilter,
  territoryFilter,
  currentPage,
  setCurrentPage,
}) => {
  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, circleFilter, territoryFilter, setCurrentPage]);

  const apiUrl = useMemo(() => {
    const params = new URLSearchParams({
      page_index: currentPage - 1,
      page_size: DEFAULT_PAGE_SIZE,
    });

    if (searchTerm) {
      params.append("search_term", searchTerm);
    }

    if (circleFilter) {
      params.append("cluster", circleFilter);
    }

    if (territoryFilter) {
      params.append("territory", territoryFilter);
    }

    return `${
      process.env.REACT_APP_API_URL
    }/api/payment_speaker/v1/psb-agency?${params.toString()}`;
  }, [currentPage, searchTerm, circleFilter, territoryFilter]);

  const {
    data: response,
    error,
    mutate,
  } = useSWR(apiUrl, inventoryFetcher, {
    fallbackData: {},
    revalidateOnFocus: false,
    revalidateOnReconnect: true,
    errorRetryCount: 3,
    errorRetryInterval: 1000,
    refreshInterval: 5 * 60 * 1000,
  });

  const distributionHouses = response?.data || [];
  const totalCount = response?.count || 0;
  const totalPages = Math.ceil(totalCount / DEFAULT_PAGE_SIZE);
  const loading = !response && !error;

  const handleViewDetails = (warehouse) => {
    onDHView(warehouse);
  };

  const renderTableContent = () => {
    if (loading) {
      return (
        <SkeletonTable
          columnCount={6}
          rowCount={7}
          headers={[
            "#",
            "Circle (Cluster)",
            "Territory",
            "Distributor",
            "Available",
            "Action",
          ]}
        />
      );
    }

    if (error) {
      return (
        <ErrorCard
          onRetryClick={mutate}
          title="Error loading distribution houses"
          message="Failed to load distribution house data. Please try again."
        />
      );
    }

    if (distributionHouses.length === 0) {
      return (
        <EmptyCard
          title="No distribution houses found"
          message={
            searchTerm
              ? "No distribution houses found matching your search criteria."
              : "No distribution house data available."
          }
        />
      );
    }

    return (
      <table className="inventory-table">
        <thead>
          <tr>
            <th>#</th>
            <th>Circle (Cluster)</th>
            <th>Territory</th>
            <th>Distributor</th>
            <th>Available</th>
            <th>Action</th>
          </tr>
        </thead>
        <tbody>
          {distributionHouses.map((item, index) => (
            <tr key={item.id} className="table-row">
              <td>{(currentPage - 1) * DEFAULT_PAGE_SIZE + index + 1}</td>
              <td>{item.territory__area__region__cluster__name || "N/A"}</td>
              <td>{item.territory__name || "N/A"}</td>
              <td title={item.name || ""}>{item.name}</td>
              <td>{item.available_devices || "-"}</td>
              <td>
                <button
                  className="details-btn"
                  onClick={() => handleViewDetails(item)}
                >
                  View Details
                  <ChevronRightIcon
                    style={{ fontSize: "16px", marginLeft: "4px" }}
                  />
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    );
  };

  return (
    <div className="inventory-content-container">
      <div className="inventory-table-container">{renderTableContent()}</div>

      {totalCount > 0 && !loading && !error && (
        <PaginationBar
          page={currentPage}
          setPage={setCurrentPage}
          totalPages={totalPages}
          totalCount={totalCount}
        />
      )}
    </div>
  );
};

export default DHList;
